import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Button,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Interview } from '@features/enterpriseEvents';
import { useDeleteInterview } from '@features/enterpriseEvents/hooks/useDeleteInterview';
import { Close, Delete } from '@mui/icons-material';
import { DeleteIcon } from '../../../../vectors/index';
import { useDeleteSpecificInterview } from '@features/applications/hooks/useDeleteSpecificInterview';
import { Application } from '@features/applications';

type DeleteInterviewModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  interview: Interview;
  onSuccess: () => void;
  handlePageNo?: (newPageNo: number) => void;

};

const DeleteInterviewModal: FC<DeleteInterviewModalProps> = ({
  isOpen,
  handleClose,
  interview,
  onSuccess,
  handlePageNo
}) => {
  const [errorText, setErrorText] = useState('');
  const [interviewStartTime, setInterviewStartTime] = useState<number>(
    dayjs(interview?.dt_start_time).valueOf() / 1000,
  );

  const { deleteInterview, isDeletingInterview } = useDeleteInterview({
    setErrorText,
    interviewStartTime,
  });
  const { deleteInterview: deleteblockInterview, isDeletingInterview: isDeletingBlockInterview } = useDeleteSpecificInterview({
    setErrorText,
  });

  const handleDelete = () => {
    const options = {
      onSuccess: (data: {
        status: number; objects: Application
      }) => {
        if (data.status === 200) {
          toast.success('Interview deleted successfully');
          handleClose();
          onSuccess();
          if (handlePageNo) {
            handlePageNo(1)
          }
        }
      },
    };
    if (interview?.is_block) {
      deleteblockInterview(
        {
          application_id: interview.application.id,
          ...(interview.application?.calendar_block_invite
            ? { calendar_block_invite: interview.application?.calendar_block_invite?.id }
            : {}),
          ...(interview.application?.calendar_item
            ? { calendar_item: interview.application?.calendar_item?.id }
            : {}),
        },
        options
      );
    } else {
      deleteInterview(
        {
          interview_id: interview.id,
          body: 'Your interview has been cancelled!',
        },
        options
      );
    }
  }
  useEffect(() => {
    if (interview) {
      setInterviewStartTime(dayjs(interview?.dt_start_time).valueOf() / 1000);
    }
  }, [interview]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <DeleteIcon />
        <br />
        <Typography variant="h4" sx={{ p: 3 }}>
          Delete Calendar Event
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ marginBottom: 3 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <Typography textAlign="center">
            Are you sure you want to delete this event? Applicants who have
            confirmed this interview will be reverted to their previous status
            and will need to be re-invited to interview.
          </Typography>
          <LoadingButton
            variant="contained"
            loading={isDeletingInterview || isDeletingBlockInterview}
            onClick={handleDelete}
            color="error"
            startIcon={<Delete />}
          >
            Yes, delete this interview
          </LoadingButton>
          <Button
            startIcon={<Close />}
            color="secondary"
            variant="outlined"
            onClick={handleClose}
          >
            No, Go Back
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteInterviewModal;
